import { PROJECT_STATUS_LABELS, TEAM_STATUS_LABELS } from '@company/shared'
import { Users, FolderKanban, ChevronRight } from 'lucide-react'

import { cn } from '@/lib/utils'

import type { TeamResourceView } from '@/hooks'

interface TeamResourceListProps {
  teams: TeamResourceView[]
  onTeamClick?: (team: TeamResourceView) => void
}

export function TeamResourceList({ teams, onTeamClick }: TeamResourceListProps) {
  if (teams.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Users className="mb-2 h-12 w-12" />
        <p>暂无团队数据</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {teams.map((team) => (
        <TeamResourceCard key={team.id} team={team} onClick={() => onTeamClick?.(team)} />
      ))}
    </div>
  )
}

interface TeamResourceCardProps {
  team: TeamResourceView
  onClick?: () => void
}

function TeamResourceCard({ team, onClick }: TeamResourceCardProps) {
  const statusColors = {
    active: 'bg-green-100 text-green-800',
    archived: 'bg-gray-100 text-gray-800',
  }

  return (
    <div
      className={cn(
        'rounded-lg border bg-card p-4 cursor-pointer transition-shadow hover:shadow-md',
        onClick && 'hover:border-primary/50'
      )}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold">{team.name}</h3>
          <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', statusColors[team.status])}>
            {TEAM_STATUS_LABELS[team.status]}
          </span>
        </div>
        {onClick && <ChevronRight className="h-5 w-5 text-muted-foreground" />}
      </div>

      {team.description && (
        <p className="text-sm text-muted-foreground mb-3">{team.description}</p>
      )}

      {/* 负责人和统计 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted font-medium">
            {team.leaderName.slice(0, 1)}
          </div>
          <div>
            <p className="text-sm font-medium">{team.leaderName}</p>
            <p className="text-xs text-muted-foreground">团队负责人</p>
          </div>
        </div>

        <div className="flex gap-4 text-sm">
          <div className="text-center">
            <p className="font-semibold">{team.memberCount}</p>
            <p className="text-xs text-muted-foreground">成员</p>
          </div>
          <div className="text-center">
            <p className="font-semibold">{team.projects.length}</p>
            <p className="text-xs text-muted-foreground">项目</p>
          </div>
        </div>
      </div>

      {/* 开发组分布 */}
      <div className="mb-4">
        <p className="mb-2 text-sm font-medium text-muted-foreground">开发组分布</p>
        <div className="flex flex-wrap gap-2">
          {team.membersByGroup
            .filter((g) => g.count > 0)
            .map((group) => (
              <span
                key={group.group}
                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs"
              >
                {group.groupLabel}
                <span className="rounded-full bg-muted px-1.5">{group.count}</span>
              </span>
            ))}
          {team.membersByGroup.every((g) => g.count === 0) && (
            <span className="text-sm text-muted-foreground">暂无开发成员</span>
          )}
        </div>
      </div>

      {/* 项目列表 */}
      {team.projects.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-medium text-muted-foreground">项目列表</p>
          <div className="space-y-1">
            {team.projects.slice(0, 3).map((project) => (
              <div
                key={project.id}
                className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-1.5"
              >
                <div className="flex items-center gap-2">
                  <FolderKanban className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{project.name}</span>
                </div>
                <ProjectStatusBadge status={project.status} />
              </div>
            ))}
            {team.projects.length > 3 && (
              <p className="text-center text-xs text-muted-foreground">
                还有 {team.projects.length - 3} 个项目...
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function ProjectStatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { className: string }> = {
    planning: { className: 'bg-blue-100 text-blue-800' },
    active: { className: 'bg-green-100 text-green-800' },
    completed: { className: 'bg-gray-100 text-gray-800' },
    archived: { className: 'bg-gray-100 text-gray-600' },
  }

  const config = statusConfig[status] ?? { className: 'bg-gray-100 text-gray-800' }
  const label = PROJECT_STATUS_LABELS[status as keyof typeof PROJECT_STATUS_LABELS] ?? status

  return (
    <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', config.className)}>
      {label}
    </span>
  )
}

/**
 * 团队详情视图
 */
interface TeamResourceDetailProps {
  team: TeamResourceView
}

export function TeamResourceDetail({ team }: TeamResourceDetailProps) {
  return (
    <div className="space-y-6">
      {/* 头部信息 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{team.name}</h2>
          {team.description && (
            <p className="mt-1 text-muted-foreground">{team.description}</p>
          )}
        </div>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-medium',
            team.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          )}
        >
          {TEAM_STATUS_LABELS[team.status]}
        </span>
      </div>

      {/* 负责人 */}
      <div className="flex items-center gap-3 rounded-lg border p-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-lg font-medium">
          {team.leaderName.slice(0, 1)}
        </div>
        <div>
          <p className="font-medium">{team.leaderName}</p>
          <p className="text-sm text-muted-foreground">团队负责人</p>
        </div>
      </div>

      {/* 成员分组 */}
      <div>
        <h3 className="mb-3 font-medium">成员分组 ({team.memberCount} 人)</h3>
        <div className="space-y-3">
          {team.membersByGroup.map((group) => (
            <div key={group.group} className="rounded-lg border p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-medium">{group.groupLabel}</span>
                <span className="rounded-full border px-2 py-0.5 text-xs">{group.count} 人</span>
              </div>
              {group.members.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {group.members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center gap-1.5 rounded-full bg-muted px-2 py-1"
                    >
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-background text-[10px] font-medium">
                        {member.name.slice(0, 1)}
                      </div>
                      <span className="text-sm">{member.name}</span>
                      {member.isLeader && (
                        <span className="rounded bg-primary/10 px-1 text-[10px] text-primary">
                          组长
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">暂无成员</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 项目列表 */}
      <div>
        <h3 className="mb-3 font-medium">项目列表 ({team.projects.length} 个)</h3>
        {team.projects.length > 0 ? (
          <div className="space-y-2">
            {team.projects.map((project) => (
              <div
                key={project.id}
                className="flex items-center justify-between rounded-lg border px-4 py-3"
              >
                <div className="flex items-center gap-2">
                  <FolderKanban className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium">{project.name}</span>
                </div>
                <ProjectStatusBadge status={project.status} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">暂无项目</p>
        )}
      </div>
    </div>
  )
}
